import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';
import { RoleMenu } from '../../../../public/assets/dashboard-config/role-map/user-role';
import { getMappedRoleForUser } from '../../../../public/assets/dashboard-config/role-map/role-map';

@Injectable({
  providedIn: 'root'
})

export class AuthGuard extends KeycloakAuthGuard {
  private readonly roleGuardLogKey = '[PAT_ROLE_MAP_GUARD_20260630]';
  private static readonly LANDING_ROUTE = '/pat/first-screen';

  constructor(
    protected override readonly router: Router,
    protected readonly keycloak: KeycloakService
  ) {
    super(router, keycloak)
  }
  async isAccessAllowed(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    if (!this.authenticated) {
      this.logRoleGuard('not_authenticated_redirecting_to_keycloak', {
        requestedUrl: state.url,
      });

      await this.keycloak.login({
        redirectUri: `${window.location.origin}${AuthGuard.LANDING_ROUTE}`,
      });
      return false;
    }

    if (this.isAccessDeniedRoute(state.url)) {
      this.logRoleGuard('access_denied_route_allowed', {
        requestedUrl: state.url,
      });
      return true;
    }

    const identity = this.getCurrentIdentity();
    const mappedRole = getMappedRoleForUser(identity);
    this.logRoleGuard('checking_role_map_access', {
      requestedUrl: state.url,
      identity,
      mappedRole,
    });

    if (!mappedRole) {
      return this.denyAccess('user_not_found_in_role_map', {
        requestedUrl: state.url,
        identity,
      });
    }

    const allowedRoutes = this.normalizeRouteList(RoleMenu?.[mappedRole]);
    if (!allowedRoutes.size) {
      return this.denyAccess('mapped_role_has_no_allowed_routes', {
        requestedUrl: state.url,
        identity,
        mappedRole,
      });
    }

    const routeCandidates = this.getRouteCandidates(state.url, route);
    const isAllowed = routeCandidates.some((candidate) => allowedRoutes.has(candidate));

    if (!isAllowed) {
      return this.denyAccess('route_not_allowed_for_mapped_role', {
        requestedUrl: state.url,
        identity,
        mappedRole,
        routeCandidates,
        allowedRoutes: Array.from(allowedRoutes),
      });
    }

    this.logRoleGuard('access_allowed', {
      requestedUrl: state.url,
      identity,
      mappedRole,
      routeCandidates,
    });

    return true;
  }

  private getCurrentIdentity(): { email?: string | null; wwid?: string | null; username?: string | null } {
    const keycloak = this.keycloak.getKeycloakInstance();
    const claims = this.getTokenClaims();
    const username = this.firstString(
      ...claims.map((claim) => claim?.preferred_username),
      ...claims.map((claim) => claim?.name),
      keycloak?.subject,
    );
    const email = this.firstString(
      ...claims.map((claim) => claim?.email),
      ...claims.map((claim) => (
        typeof claim?.preferred_username === 'string' && claim.preferred_username.includes('@')
          ? claim.preferred_username
          : null
      )),
    );

    return {
      email,
      username,
      wwid: this.firstString(
        ...claims.map((claim) => claim?.wwid),
        ...claims.map((claim) => claim?.WWID),
        ...claims.map((claim) => claim?.employee_id),
        ...claims.map((claim) => claim?.employeeId),
        username,
      ),
    };
  }

  private getTokenClaims(): any[] {
    return [
      this.keycloak.getKeycloakInstance()?.tokenParsed,
      this.keycloak.getKeycloakInstance()?.idTokenParsed,
    ].filter((claim): claim is Record<string, unknown> => Boolean(claim && typeof claim === 'object'));
  }

  private getRouteCandidates(url: string, route: ActivatedRouteSnapshot): string[] {
    const path = this.cleanPath(url);
    const segments = path.split('/').filter(Boolean);
    const candidates = new Set<string>([path]);

    if (segments.length > 1) {
      candidates.add(`/${segments.slice(1).join('/')}`);
    }

    if (segments.length) {
      candidates.add(`/${segments[segments.length - 1]}`);
    }

    const configFile = String(route.data?.['configFile'] || '').trim();
    if (configFile) {
      const configPath = this.cleanPath(configFile.replace(/\.json$/i, ''));
      const configSegments = configPath.split('/').filter(Boolean);
      candidates.add(configPath);
      if (configSegments.length > 1) {
        candidates.add(`/${configSegments.slice(1).join('/')}`);
      }
      if (configSegments.length) {
        candidates.add(`/${configSegments[configSegments.length - 1]}`);
      }
    }

    return Array.from(candidates);
  }

  private normalizeRouteList(routes: unknown): Set<string> {
    if (!Array.isArray(routes)) {
      return new Set();
    }

    return new Set(
      routes
        .map((route) => this.cleanPath(String(route || '')))
        .filter(Boolean),
    );
  }

  private cleanPath(value: string): string {
    const trimmed = String(value || '').trim().split('?')[0].split('#')[0].replace(/\\/g, '/');
    const withoutAssetsPrefix = trimmed.replace(/^assets\/dashboard-config\//, '');
    const withoutJson = withoutAssetsPrefix.replace(/\.json$/i, '');
    const normalized = withoutJson.startsWith('/') ? withoutJson : `/${withoutJson}`;
    return normalized.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
  }

  private isAccessDeniedRoute(url: string): boolean {
    return this.cleanPath(url) === '/access-denied';
  }

  private denyAccess(reason: string, details: Record<string, unknown>): UrlTree {
    this.logRoleGuard(`access_denied_${reason}`, details, true);
    return this.router.parseUrl('/access-denied');
  }

  private logRoleGuard(message: string, details: Record<string, unknown> = {}, warn = false): void {
    const log = warn ? console.warn : console.info;
    log(this.roleGuardLogKey, message, details);
  }

  private firstString(...values: unknown[]): string | null {
    for (const value of values) {
      const text = String(value || '').trim();
      if (text) {
        return text;
      }
    }

    return null;
  }
}
